import { createFileRoute } from "@tanstack/react-router";
import { ServicePageTemplate } from "@/components/ServicePageTemplate";
import type { Service } from "@/data/services";
import { Server } from "lucide-react";
import { canonical, seoCanonicalLink, seoOgUrl } from "@/lib/seo";
import claremontHero from "@/assets/managed-it-claremont-ca.png";

const PATH = "/managed-it-services-claremont-ca";
const TITLE = "Managed IT Services Claremont CA | SimpleTech Helper";
const DESCRIPTION =
  "Looking for managed IT services in Claremont, CA? SimpleTech Helper provides proactive IT support, cybersecurity & more. Call (909) 255-9112.";

const claremontService: Service = {
  slug: "managed-it",
  title: "Managed IT Services in Claremont, CA",
  shortTitle: "Managed IT",
  excerpt:
    "Proactive IT management for Claremont, CA businesses — local support, cybersecurity, and dependable technology.",
  description:
    "Running a small business in Claremont, CA means wearing a lot of hats. IT problems shouldn't be one of them. SimpleTech Helper delivers managed IT services Claremont CA businesses can count on — proactive monitoring, fast support, cybersecurity, and a real local team that picks up the phone. No more waiting days for a callback. No more paying emergency rates every time something breaks.",
  icon: Server,
  highlights: [
    "24/7 proactive monitoring and patching",
    "Data backup and disaster recovery",
    "Cloud strategy, migration and optimization",
    "Vendor management and IT roadmap planning",
  ],
  seoDescription:
    "Managed IT services for Claremont, CA businesses — proactive monitoring, cybersecurity, cloud, and local expert support from SimpleTech Helper.",
  heroSubtitle: "Local Managed IT for Claremont, CA Businesses",
  heroBestFor:
    "Best for Claremont, CA small offices, multi-site teams, and compliance-minded businesses.",
  heroImage: claremontHero,
  heroImageClass: "max-h-[480px] max-w-[640px]",
  heroImagePosition: "right-0 bottom-0",
};

const FAQS = [
  {
    q: "How quickly does SimpleTech Helper respond to IT issues in Claremont, CA?",
    a: "Critical issues are addressed immediately. Non-critical helpdesk requests are typically resolved same day. SimpleTech Helper's local team can be on-site in Claremont, CA when remote support isn't enough.",
  },
  {
    q: "Do we need to sign a long-term contract?",
    a: "No. SimpleTech Helper offers flexible managed IT agreements for Claremont, CA businesses. We prefer to earn your renewal every month.",
  },
  {
    q: "What size business is managed IT right for?",
    a: "SimpleTech Helper serves Claremont, CA businesses with as few as 2 employees up to 50+. The value of managed IT scales well even for very small teams — one avoided incident often pays for several months of service.",
  },
  {
    q: "Can SimpleTech Helper take over IT from our current provider?",
    a: "Yes. SimpleTech Helper handles the full transition from your current IT provider, including documentation, asset inventory, and onboarding — with minimal disruption to your Claremont, CA operations.",
  },
  {
    q: "Does managed IT include cybersecurity?",
    a: "Every SimpleTech Helper managed IT plan includes baseline cybersecurity (endpoint protection, MFA, patch management, email filtering). Businesses that need more advanced protection can add our full cybersecurity services.",
  },
];

export const Route = createFileRoute("/managed-it-services-claremont-ca")({
  component: () => <ServicePageTemplate service={claremontService} />,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      {
        name: "keywords",
        content:
          "managed IT services Claremont CA, IT support Claremont, Claremont MSP, business IT Claremont, IT company Claremont Inland Empire, (909) 255-9112",
      },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      seoOgUrl(PATH),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "geo.region", content: "US-CA" },
      { name: "geo.placename", content: "Claremont" },
      { name: "geo.position", content: "34.0967;-117.7198" },
      { name: "ICBM", content: "34.0967, -117.7198" },
    ],
    links: [seoCanonicalLink(PATH)],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "SimpleTech Helper — Managed IT Services Claremont",
          telephone: "+1-909-255-9112",
          email: "contact@simpletechhelper.com",
          url: canonical(PATH),
          priceRange: "$$",
          address: {
            "@type": "PostalAddress",
            streetAddress: "8880 N Benson Ave #121",
            addressLocality: "Montclair",
            addressRegion: "CA",
            postalCode: "91763",
            addressCountry: "US",
          },
          areaServed: { "@type": "City", name: "Claremont" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: canonical("/") },
            { "@type": "ListItem", position: 2, name: "Services", item: canonical("/services") },
            {
              "@type": "ListItem",
              position: 3,
              name: "Managed IT Services Claremont, CA",
              item: canonical(PATH),
            },
          ],
        }),
      },
    ],
  }),
});
